import React, { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";

/**
 * Displays a "live" stock pulse + current stock count.
 * Animates briefly when the stock value changes (i.e. a real-time decrement).
 *
 * Props:
 * - qty: current stock quantity (number)
 * - status: 'in_stock' | 'low_stock' | 'out_of_stock'
 * - compact: smaller variant for grid cards
 * - testid: optional data-testid
 */
export default function LiveStockBadge({ qty, status, compact = false, testid }) {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(qty);

  useEffect(() => {
    if (prevRef.current !== qty && prevRef.current !== undefined) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1400);
      return () => clearTimeout(t);
    }
    prevRef.current = qty;
  }, [qty]);

  if (qty === undefined || qty === null) return null;

  const isOOS = status === "out_of_stock" || qty <= 0;
  const isLow = !isOOS && qty <= 10;

  // Color tiers
  const tier = isOOS
    ? { dot: "#9CA8B8", text: "#9CA8B8", bg: "rgba(156,168,184,0.08)", border: "rgba(156,168,184,0.3)" }
    : isLow
    ? { dot: "#FF6B3B", text: "#FF8A5B", bg: "rgba(255,107,59,0.08)", border: "rgba(255,107,59,0.4)" }
    : { dot: "#A6FF00", text: "#A6FF00", bg: "rgba(166,255,0,0.07)", border: "rgba(166,255,0,0.35)" };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm border transition-all ${flash ? "pg-stock-flash" : ""} ${compact ? "px-1.5 py-0.5" : "px-2 py-1"}`}
      style={{ background: tier.bg, borderColor: tier.border }}
      data-testid={testid || "live-stock-badge"}
      title="Live UK stock"
    >
      <span className="relative flex items-center">
        <span
          className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping"
          style={{ background: tier.dot }}
        />
        <span
          className="relative inline-flex rounded-full"
          style={{ background: tier.dot, width: compact ? 5 : 6, height: compact ? 5 : 6 }}
        />
      </span>
      <Activity size={compact ? 9 : 10} style={{ color: tier.text }} />
      <span
        className={`pg-mono uppercase tracking-[0.18em] font-bold ${compact ? "text-[8.5px]" : "text-[10px]"}`}
        style={{ color: tier.text }}
      >
        {isOOS ? "Sold out" : isLow ? `Live · ${qty} in UK stock` : "Live · In UK stock"}
      </span>
    </span>
  );
}
